import { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import GroupsIcon from '@mui/icons-material/Groups'
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism'
import VisibilityIcon from '@mui/icons-material/Visibility'
import CampaignIcon from '@mui/icons-material/Campaign'
import HowToVoteIcon from '@mui/icons-material/HowToVote'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import AutoStoriesIcon from '@mui/icons-material/AutoStories'
import InsightsIcon from '@mui/icons-material/Insights'
import RefreshIcon from '@mui/icons-material/Refresh'
import { baseUrl, config } from 'features/constants'
import { SettingsMainContainer, TopHeaderSettings } from '../settings.styled'
import { useTranslation } from 'react-i18next'

const rangeOptions = [
  { value: '7d', days: 7 },
  { value: '30d', days: 30 },
  { value: 'all' },
]

const emptyAnalytics = {
  activeMembers: 0,
  engagedMembers: 0,
  prayersCreated: 0,
  prayerViews: 0,
  prayerCommitments: 0,
  announcementViews: 0,
  pollVotes: 0,
  eventRsvps: 0,
  eventViews: 0,
  testimoniesCreated: 0,
}

const buildDateQuery = (range) => {
  const option = rangeOptions.find((item) => item.value === range)

  if (!option?.days) {
    return ''
  }

  const to = new Date()
  const from = new Date()
  from.setDate(to.getDate() - option.days)

  const params = new URLSearchParams({
    from: from.toISOString(),
    to: to.toISOString(),
  })

  return `?${params.toString()}`
}

function Engagement() {
  const { t } = useTranslation('settings')
  const { user } = useSelector((state) => state.auth)
  const [selectedRange, setSelectedRange] = useState('30d')
  const [analytics, setAnalytics] = useState(emptyAnalytics)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const loadAnalytics = useCallback(async () => {
    if (!user?._id || !user?.token || !user?.isOrganization) {
      return
    }

    try {
      setIsLoading(true)
      setError('')
      const dateQuery = buildDateQuery(selectedRange)
      const response = await axios.get(
        `${baseUrl}/api/churches/${user._id}/analytics${dateQuery}`,
        config(user.token)
      )

      setAnalytics({
        ...emptyAnalytics,
        ...response.data,
      })
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Unable to load engagement analytics.')
    } finally {
      setIsLoading(false)
    }
  }, [selectedRange, user?._id, user?.isOrganization, user?.token])

  useEffect(() => {
    loadAnalytics()
  }, [loadAnalytics])

  const metrics = useMemo(() => ([
    {
      label: t('engagement.metrics.activeMembers'),
      value: analytics.activeMembers,
      icon: <GroupsIcon />,
    },
    {
      label: t('engagement.metrics.engagedMembers'),
      value: analytics.engagedMembers,
      icon: <InsightsIcon />,
    },
    {
      label: t('engagement.metrics.prayersCreated'),
      value: analytics.prayersCreated,
      icon: <VolunteerActivismIcon />,
    },
    {
      label: t('engagement.metrics.prayerViews'),
      value: analytics.prayerViews,
      icon: <VisibilityIcon />,
    },
    {
      label: t('engagement.metrics.prayerCommitments'),
      value: analytics.prayerCommitments,
      icon: <VolunteerActivismIcon />,
    },
    {
      label: t('engagement.metrics.announcementViews'),
      value: analytics.announcementViews,
      icon: <CampaignIcon />,
    },
    {
      label: t('engagement.metrics.pollVotes'),
      value: analytics.pollVotes,
      icon: <HowToVoteIcon />,
    },
    {
      label: t('engagement.metrics.eventRsvps'),
      value: analytics.eventRsvps,
      icon: <EventAvailableIcon />,
    },
    {
      label: t('engagement.metrics.eventViews'),
      value: analytics.eventViews,
      icon: <VisibilityIcon />,
    },
    {
      label: t('engagement.metrics.testimoniesCreated'),
      value: analytics.testimoniesCreated,
      icon: <AutoStoriesIcon />,
    },
  ]), [analytics, t])

  if (!user?.isOrganization) {
    return (
      <SettingsMainContainer>
        <TopHeaderSettings>{t('engagement.title')}</TopHeaderSettings>
        <EngagementBody>
          <StateCard>{t('engagement.churchOnly')}</StateCard>
        </EngagementBody>
      </SettingsMainContainer>
    )
  }

  return (
    <SettingsMainContainer>
      <TopHeaderSettings>
        <HeaderRow>
          <div>
            <Title>{t('engagement.title')}</Title>
            <Subtitle>{t('engagement.subtitle')}</Subtitle>
          </div>
          <RefreshButton onClick={loadAnalytics} disabled={isLoading}>
            <RefreshIcon fontSize="small" />
            {t('engagement.refresh')}
          </RefreshButton>
        </HeaderRow>
      </TopHeaderSettings>

      <EngagementBody>
        <RangeTabs>
          {rangeOptions.map((option) => (
            <RangeButton
              key={option.value}
              active={selectedRange === option.value}
              onClick={() => setSelectedRange(option.value)}
              type="button"
            >
              {t(`engagement.ranges.${option.value}`)}
            </RangeButton>
          ))}
        </RangeTabs>

        {error && (
          <StateCard>
            <StateTitle>{t('engagement.loadError')}</StateTitle>
            <StateText>{error}</StateText>
            <RetryButton onClick={loadAnalytics} type="button">{t('engagement.retry')}</RetryButton>
          </StateCard>
        )}

        {isLoading && !error ? (
          <StateCard>{t('engagement.loading')}</StateCard>
        ) : (
          <MetricsGrid>
            {metrics.map((metric) => (
              <MetricCard key={metric.label}>
                <MetricIcon>{metric.icon}</MetricIcon>
                <MetricContent>
                  <MetricValue>{Number(metric.value || 0).toLocaleString()}</MetricValue>
                  <MetricLabel>{metric.label}</MetricLabel>
                </MetricContent>
              </MetricCard>
            ))}
          </MetricsGrid>
        )}
      </EngagementBody>
    </SettingsMainContainer>
  )
}

export default Engagement

const EngagementBody = styled.div`
  width: 100%;
  padding: 0 20px 30px;
  display: flex;
  flex-direction: column;
  gap: 18px;

  @media (max-width: 768px) {
    padding: 0 10px 30px;
  }
`

const HeaderRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 768px) {
    align-items: flex-start;
    flex-direction: column;
  }
`

const Title = styled.div`
  color: var(--lightThemeFontColor);
  font-family: 'Roboto', sans-serif;
  font-size: 28px;
  font-weight: 500;
`

const Subtitle = styled.div`
  margin-top: 6px;
  color: var(--fontColorGrey);
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
`

const RefreshButton = styled.button`
  height: 40px;
  border: none;
  border-radius: 8px;
  background-color: var(--themeColor);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 14px;
  font-family: 'Roboto', sans-serif;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`

const RangeTabs = styled.div`
  width: fit-content;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px;
  border-radius: 12px;
  background-color: white;
  box-shadow: 0px 0px 10px #ddd;

  @media (max-width: 768px) {
    width: 100%;
  }
`

const RangeButton = styled.button`
  height: 34px;
  border: 1px solid ${props => props.active ? 'var(--themeColor)' : 'transparent'};
  border-radius: 8px;
  background-color: ${props => props.active ? 'var(--themeColor)' : 'transparent'};
  color: ${props => props.active ? 'white' : 'var(--lightThemeFontColor)'};
  padding: 0 14px;
  font-family: 'Roboto', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: var(--themeColor);
  }

  @media (max-width: 768px) {
    flex: 1;
    padding: 0 8px;
  }
`

const MetricsGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 14px;
`

const MetricCard = styled.div`
  min-height: 110px;
  border: 1px solid var(--borderColor);
  border-radius: 12px;
  background-color: white;
  box-shadow: 0px 0px 10px #e1e1e1;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
`

const MetricIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background-color: rgba(45, 128, 255, 0.1);
  color: var(--themeColor);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const MetricContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const MetricValue = styled.div`
  color: var(--lightThemeFontColor);
  font-family: 'Roboto', sans-serif;
  font-size: 28px;
  font-weight: 600;
`

const MetricLabel = styled.div`
  color: var(--fontColorGrey);
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
`

const StateCard = styled.div`
  width: 100%;
  border: 1px solid var(--borderColor);
  border-radius: 12px;
  background-color: white;
  box-shadow: 0px 0px 10px #e1e1e1;
  color: var(--lightThemeFontColor);
  font-family: 'Roboto', sans-serif;
  padding: 20px;
  text-align: left;
`

const StateTitle = styled.div`
  color: var(--lightThemeFontColor);
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 6px;
`

const StateText = styled.div`
  color: var(--fontColorGrey);
  font-size: 13px;
  margin-bottom: 14px;
`

const RetryButton = styled.button`
  height: 36px;
  border: none;
  border-radius: 8px;
  background-color: var(--themeColor);
  color: white;
  padding: 0 14px;
  font-family: 'Roboto', sans-serif;
  cursor: pointer;
`
